import { z } from 'zod'

/**
 * AI 기능 필수 동의 (2026-10-29 대표 결정: "AI 사용 동의는 무조건 의무로 한다").
 * 생성·AI 수정·사진·맛 예측·번역·비교 제품 검색처럼 입력을 외부 AI 로 보내는 기능은 지금 판의 안내문에 동의한 사람만 쓴다.
 * 동의하지 않아도 AI 를 쓰지 않는 기능(커뮤니티·완성 사례·만든 결과 보기·시식 기록·계정)은 그대로다.
 *
 * 기록은 회원은 계정, 손님은 기기 UUID(X-Device-Id) 단위로 서버에 둔다 (광고 동의 marketing-consent 와 같은 구조).
 * 1.0.7 까지의 앱은 기기에만 동의를 저장해(AsyncStorage consent:ai:v1) 서버가 모른다 — 그래서 서버 강제는
 * 관리자 스위치 두 개(aiConsentSettingsSchema)로 나눠 단계적으로 켠다. 켜는 순서는 그 스키마 설명에 있다
 */

/**
 * 동의 안내문의 판. 앱 동의 화면(apps/mobile/src/components/ai-consent-modal.tsx)의 보내는 것·받는 곳·목적이나
 * 처리방침의 '타사 AI 처리' 절이 실질적으로 바뀌면 1 올린다 — 올리면 모든 사람에게 다시 묻는다.
 * 1 = 기기에만 저장하던 옛 동의(~1.0.7). 서버 기록은 2 부터다
 */
export const AI_CONSENT_VERSION = 2

/** unknown = 고른 적 없음(옛 앱 포함), outdated = 옛 판에 동의해 다시 물어야 함, denied = 동의하지 않음·철회 */
export type AiConsentStatus = 'unknown' | 'granted' | 'denied' | 'outdated'

export interface AiConsentRecord {
  granted: boolean
  version: number
}

/** 저장된 기록의 지금 상태. 서버(요청 허용 판단·응답)가 쓴다 */
export function aiConsentStatus(record: AiConsentRecord | null | undefined): AiConsentStatus {
  if (!record) return 'unknown'
  if (!record.granted) return 'denied'
  return record.version >= AI_CONSENT_VERSION ? 'granted' : 'outdated'
}

/** GET·PUT /me/ai-consent 응답 */
export interface AiConsentView {
  status: AiConsentStatus
  /** 서버가 지금 요구하는 안내문의 판 */
  version: number
  /** 마지막으로 동의·거부·철회한 시각 */
  decidedAt: string | null
}

/** PUT /me/ai-consent — 동의·거부·철회 */
export const aiConsentUpdateSchema = z.object({
  granted: z.boolean(),
  /** 앱이 보여 준 안내문의 판. 서버가 모르는 더 새 판은 받지 않는다 */
  version: z.number().int().min(2).max(AI_CONSENT_VERSION),
  /** prompt = AI 기능을 쓰기 전에 뜨는 동의 화면, settings = 계정 탭의 철회 */
  source: z.enum(['prompt', 'settings']),
})
export type AiConsentUpdate = z.infer<typeof aiConsentUpdateSchema>

/**
 * 관리자 [설정] › AI 동의. 서버 코드 쪽 스위치(GET /config 의 features.aiConsent)와 두 겹이고, 둘 다 기본 꺼짐이다.
 * 켜는 순서 (전원 재동의 완성):
 *  1. 서버 배포 — 아무것도 바뀌지 않는다 (둘 다 꺼짐).
 *  2. 서버 기록을 쓰는 앱(X-Client-Features 에 ai-consent)을 스토어에 낸다.
 *  3. required 켬 — 새 앱은 AI 기능을 쓰기 전에 지금 판 동의를 서버에 남기게 하고, 서버는 새 앱의 미동의 요청과
 *     지금 판을 거부·철회한 사람의 요청을 거절한다. 옛 앱(1.0.3~1.0.7)은 기기에 남긴 옛 동의로 지금처럼 쓴다.
 *  4. 새 앱이 충분히 퍼지면 관리자 새 버전 안내의 최소 버전(appUpdate.minVersion)을 새 앱으로 올려 옛 앱을 업데이트로 보낸다.
 *  5. legacyAppsRequired 켬 — 서버가 옛 앱의 요청도 지금 판 동의 기록이 없으면 거절한다. 여기서 전원 재동의가 끝난다
 */
export const aiConsentSettingsSchema = z.object({
  /** 새 앱의 필수 재동의와 서버 강제(새 앱·명시 거부). 끄면 예전처럼 기기 동의만 본다 */
  required: z.boolean().default(false),
  /** 옛 앱 요청도 서버 기록으로 막는다. required 가 켜져 있어야 효과가 있다. 최소 버전을 올린 뒤에만 켠다 */
  legacyAppsRequired: z.boolean().default(false),
})
export type AiConsentSettings = z.infer<typeof aiConsentSettingsSchema>
export const DEFAULT_AI_CONSENT_SETTINGS: AiConsentSettings = {
  required: false,
  legacyAppsRequired: false,
}

/** 서버 동의 기록을 남기고 필수 동의 화면을 띄울 줄 아는 앱이 X-Client-Features 에 싣는 값 */
export const CLIENT_FEATURE_AI_CONSENT = 'ai-consent'
/** 지금 판 AI 동의가 없어 거절한 요청(403)의 error.code. AI 로 아무것도 보내지 않았고 사용권도 쓰지 않았다 */
export const AI_CONSENT_REQUIRED_CODE = 'AI_CONSENT_REQUIRED'
